"use client";

import { useContext, useMemo } from "react";
import { useAtomValue } from "jotai";

import {
  graphNodesAtom,
  nodeRegistryAtom,
  activeFlowIdAtom,
  dynamicNodeConfigsAtom,
} from "@/store/editor-v2";
import type { NodeDefinition } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import {
  collectNodeBindingsFromConfig,
  type NodeBinding,
} from "./variableRefs";
import { useSecretsSources } from "./useSecretsSources";
import { RunDefinitionFallbackContext } from "./RunDefinitionFallbackContext";
import type { SecretLookupEntry } from "./mentionSource";
import { isConfigEntryVisible } from "@/components/Form/configVisibility";
import type { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";

export type { NodeBinding };

/**
 * Drop config fields hidden by their visibility rules before collecting refs.
 * A field shown only for a specific option (e.g. List Operations' separator,
 * visible only for "split value to list") keeps its `:var[...]` value when
 * hidden, but the node doesn't consume it — so it must not surface as a binding
 * (it would otherwise show up as a phantom input on the run form). Mirrors the
 * config panel's own `isConfigEntryVisible` check.
 */
function visibleConfigFields(
  nodeConfig: Record<string, unknown>,
  configDefs: Record<string, NoxusFormEntryConfig>
): Record<string, unknown> {
  const resolveValue = (configSource: string): unknown => {
    const raw = nodeConfig[configSource];
    return raw !== undefined ? raw : configDefs[configSource]?.default;
  };
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(nodeConfig)) {
    const def = configDefs[key];
    if (def && !isConfigEntryVisible(def, resolveValue)) continue;
    out[key] = value;
  }
  return out;
}

export function useNodeBindings(nodeId: string): NodeBinding[] {
  const nodes = useAtomValue(graphNodesAtom);
  const registry = useAtomValue(nodeRegistryAtom) as NodeDefinition[];
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const dynamicNodeConfigs = useAtomValue(dynamicNodeConfigsAtom);
  const fallback = useContext(RunDefinitionFallbackContext);
  const { spotGroup } = useSpotUser();
  const { secrets } = useSecretsSources(spotGroup?.id, activeFlowId);

  const secretsByQualifiedId = useMemo(() => {
    const m = new Map<string, SecretLookupEntry>();
    for (const s of secrets) m.set(`${s.scope}:${s.id}`, s);
    return m;
  }, [secrets]);

  return useMemo(() => {
    // Outside the editor (e.g. the case view's run modal) the editor atoms are
    // empty or hold a different flow — resolve against the run's own
    // definition supplied via context instead.
    const effectiveNodes = nodes.some((n) => n.id === nodeId)
      ? nodes
      : fallback.nodes;
    const effectiveRegistry =
      registry.length > 0 ? registry : fallback.registry;
    const me = effectiveNodes.find((n) => n.id === nodeId);
    if (!me) return [];
    const config = (me.data?.nodeConfig ?? {}) as Record<string, unknown>;
    const registryEntry = effectiveRegistry.find(
      (r) => r.type === me.data?.nodeType
    );
    const configDefs = {
      ...(registryEntry?.config ?? {}),
      ...(dynamicNodeConfigs.get(nodeId)?.config ?? {}),
    } as Record<string, NoxusFormEntryConfig>;
    return collectNodeBindingsFromConfig(
      visibleConfigFields(config, configDefs),
      effectiveNodes,
      effectiveRegistry,
      secretsByQualifiedId,
      dynamicNodeConfigs
    );
  }, [
    nodes,
    registry,
    nodeId,
    secretsByQualifiedId,
    dynamicNodeConfigs,
    fallback,
  ]);
}
